import { FunctionComponent, useEffect, useMemo, useState, MutableRefObject } from "react";

import { Information } from "@/components/Information/Information";
import { LinkedinEditor } from "@/pages/Private/pages/Account/components/LinkedinEditor";

import { Toggle } from "@/components/Toggle/Toggle";

import { Button } from "@/components/Button/Button";

import { ColumnConfig } from "../../../schema/columnConfig";
import { EventOutreachSchemaType } from "../../../schema/list";

import {
	EmailNodeConfig,
	LinkedinInmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
} from "./nodes";

import { MyNode } from "../CampaignOverview";

import { Sender } from "./Sender";
import { LinkedinVisitNodeConfig } from "./nodes/LinkedinVisitNode";

export const LinkedinStandardInvite: FunctionComponent<{
	currentLeadIndex: number;
	setSelectedNode: (node: EmailNodeConfig) => void;
	selectedNode:
		| MyNode
		| EmailNodeConfig
		| LinkedinInviteNodeConfig
		| LinkedinNodeConfig
		| LinkedinInmailNodeConfig
		| LinkedinVisitNodeConfig
		| null;
	nodesRef: MutableRefObject<MyNode[]>;
	sender?: Sender;
	eventBasedState: boolean;
	setIsDirty?: (isDirty: boolean) => void;
	onUpdate?: () => void;
	listColumns?: ColumnConfig[];
	listEvent?: EventOutreachSchemaType | null;
	listId?: number;
	onCreateSpintax?: () => void;
	onCreateAiSnippet?: () => void;
	showAiSnippetButton?: boolean;
}> = ({
	currentLeadIndex,
	setSelectedNode,
	selectedNode,
	nodesRef,
	sender,
	eventBasedState,
	setIsDirty,
	onUpdate,
	listColumns,
	listEvent,
	listId,
	onCreateSpintax,
	onCreateAiSnippet,
	showAiSnippetButton,
}) => {
	const [showEditor, setShowEditor] = useState<boolean>(false);

	const currentValue = useMemo(() => {
		return eventBasedState
			? (selectedNode?.data.alternateMessage ?? "")
			: (selectedNode?.data.message ?? "");
	}, [selectedNode, eventBasedState]);

	useEffect(() => {
		if (currentValue) {
			setShowEditor(true);
		}
	}, [currentValue]);

	const updateText = (value: string) => {
		if (eventBasedState) {
			setSelectedNode({
				...selectedNode,
				data: {
					...selectedNode?.data,
					alternateMessage: value,
				},
			} as EmailNodeConfig);
		} else {
			setSelectedNode({
				...selectedNode,
				data: {
					...selectedNode?.data,
					message: value,
				},
			} as EmailNodeConfig);
		}

		nodesRef.current = nodesRef.current.map((node) => {
			if (node.id === selectedNode?.id) {
				if (eventBasedState) {
					return { ...node, data: { ...node.data, alternateMessage: value } };
				} else {
					return { ...node, data: { ...node.data, message: value } };
				}
			}

			return node;
		});

		if (
			(!eventBasedState && value !== selectedNode?.data.message) ||
			(eventBasedState && value !== selectedNode?.data.alternateMessage)
		) {
			setIsDirty?.(true);
			onUpdate?.();
		}
	};

	return (
		<>
			<div className="text-sm whitespace-nowrap flex flex-col gap-1 items-start mr-4 mb-2 mt-2">
				<div className="flex flex-row items-center gap-2">
					<div>
						<Toggle isChecked={showEditor} onChange={() => setShowEditor(!showEditor)} />
					</div>
					<div>
						<div>
							<div className="flex flex-row items-center gap-1">
								Optional: As Linkedin Basic Account user:
								<Information
									information={
										"Linkedin Users with a free Basic plan can typically only send 5 connection requests with a message per month"
									}
								/>
							</div>
						</div>
						<div className="flex flex-row items-center">
							Add a note to your invitation (max. 200 characters)
						</div>
					</div>
				</div>
			</div>
			{showEditor && (
				<LinkedinEditor
					currentLeadIndex={currentLeadIndex}
					handleChange={(value: string) => {
						updateText(value);
					}}
					handleInputChange={() => {
						setIsDirty?.(true);
					}}
					listColumns={listColumns}
					listEvent={listEvent}
					listId={listId}
					maxLength={200}
					placeholder="Write your message here"
					sender={sender}
					showAiSnippetButton={showAiSnippetButton}
					signature={currentValue}
					onCreateAiSnippet={onCreateAiSnippet}
					onCreateSpintax={onCreateSpintax}
				/>
			)}
			{!showEditor && currentValue.length > 0 && (
				<div className="flex flex-col gap-1">
					<div className="rounded-lg bg-status-warning-light font-semibold px-2 py-1 text-status-warning text-sm flex flex-col gap-1 items-start mr-4 mb-2 mt-2">
						Warning: you have added a note to your invitation, but the toggle is off. The note will
						be sent.
					</div>
					<div className="w-fit">
						<Button className="w-full" title="Remove note" onClick={() => updateText("")} />
					</div>
				</div>
			)}
		</>
	);
};
